// Banner (DESIGN_SYSTEM §9.1): pending activation (amber, what's left and who acts), plan-locked
// (in place, never a redirect), permission, test mode, offline, info and error. A pending feature is
// never labelled Live, Active or Connected until server-verified.
import type { ReactNode } from "react";
import { t } from "@ady/i18n";
import { cx } from "../lib";
import type { ActivationStep, Tone } from "../state";
import { toneClasses } from "../tones";
import { Button } from "./Button";
import { Icon, type IconName } from "./Icon";

export type BannerKind =
  | "pending-activation"
  | "plan-locked"
  | "permission-denied"
  | "test-mode"
  | "offline"
  | "info"
  | "success"
  | "error";

const look: Record<BannerKind, { tone: Tone; icon: IconName }> = {
  "pending-activation": { tone: "warning", icon: "clock" },
  "plan-locked": { tone: "action", icon: "lock" },
  "permission-denied": { tone: "neutral", icon: "lock" },
  "test-mode": { tone: "warning", icon: "triangle" },
  offline: { tone: "warning", icon: "pulse" },
  info: { tone: "action", icon: "info" },
  success: { tone: "success", icon: "check" },
  error: { tone: "danger", icon: "octagon" },
};

export interface BannerProps {
  kind: BannerKind;
  title: ReactNode;
  children?: ReactNode;
  steps?: ActivationStep[];
  action?: { label: string; onClick: () => void; "data-testid"?: string };
  onDismiss?: () => void;
  className?: string;
  "data-testid"?: string;
}

export function Banner({
  kind,
  title,
  children,
  steps,
  action,
  onDismiss,
  className,
  "data-testid": testId,
}: BannerProps) {
  const { tone, icon } = look[kind];
  const c = toneClasses[tone];
  return (
    <div
      role={kind === "error" ? "alert" : "status"}
      data-kind={kind}
      data-testid={testId}
      className={cx(
        "flex items-start gap-3 rounded-md border px-4 py-3",
        c.bg,
        c.border,
        className,
      )}
    >
      <Icon name={icon} className={cx("mt-0.5 flex-none", c.fg)} />
      <div className="flex flex-1 flex-col gap-1">
        <p className={cx("text-md font-semibold", c.fg)}>{title}</p>
        {children ? <div className="text-sm text-fg">{children}</div> : null}
        {steps?.length ? (
          <ol className="mt-1 flex flex-col gap-1 text-sm">
            {steps.map((s, i) => (
              <li key={i} className="flex items-center gap-2">
                <Icon
                  name={s.done ? "check" : "clock"}
                  size={16}
                  className={s.done ? "text-success-fg" : c.fg}
                />
                <span className={cx(s.done && "line-through text-fg-secondary")}>{s.label}</span>
              </li>
            ))}
          </ol>
        ) : null}
        {action ? (
          <div className="pt-1">
            <Button
              variant="secondary"
              size="md"
              onClick={action.onClick}
              data-testid={action["data-testid"]}
            >
              {action.label}
            </Button>
          </div>
        ) : null}
      </div>
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          className="-me-2 -mt-1.5 flex size-11 flex-none items-center justify-center rounded-sm text-fg-secondary hover:bg-hover"
          aria-label={t("common.dismiss")}
        >
          <Icon name="x" size={18} />
        </button>
      ) : null}
    </div>
  );
}
